import { z } from 'zod';

/**
 * Plan keys as stored in D1 and used on every surface.
 *
 * Supporter is the only subscription. Crew, its seats and its guests were removed: Access now
 * comes from a membership or from being staff, and nothing else grants it.
 */
export const PLAN_KEYS = ['supporter_month', 'supporter_year'] as const;
export const planKeySchema = z.enum(PLAN_KEYS);
export type PlanKey = z.infer<typeof planKeySchema>;

/** The two plans a member can buy, which is all of them. */
export const PURCHASABLE_PLAN_KEYS = ['supporter_month', 'supporter_year'] as const;
export const purchasablePlanKeySchema = z.enum(PURCHASABLE_PLAN_KEYS);
export type PurchasablePlanKey = z.infer<typeof purchasablePlanKeySchema>;

export type PlanTier = 'supporter';
export type PlanInterval = 'month' | 'year';

export interface PlanDefinition {
  key: PurchasablePlanKey;
  tier: PlanTier;
  interval: PlanInterval;
  /** Price in cents, EUR, VAT included. What the buyer is charged is what is shown. */
  priceCents: number;
  currency: 'EUR';
}

/**
 * Prices, VAT included.
 *
 * The yearly price used to be exactly ten months, so `MONTHS_FREE` fell out of the division
 * cleanly. It no longer is: 14.99 is about seven and a half months of 1.99, so the annual
 * discount is real but not a round number. Anything deriving a month count from these two has
 * to round -- see `MONTHS_FREE` in the client.
 */
export const PLANS: Record<PurchasablePlanKey, PlanDefinition> = {
  supporter_month: {
    key: 'supporter_month',
    tier: 'supporter',
    interval: 'month',
    priceCents: 199,
    currency: 'EUR',
  },
  supporter_year: {
    key: 'supporter_year',
    tier: 'supporter',
    interval: 'year',
    priceCents: 1499,
    currency: 'EUR',
  },
};

export const PURCHASABLE_PLANS: PlanDefinition[] = PURCHASABLE_PLAN_KEYS.map((k) => PLANS[k]);

export function planTier(key: PlanKey): PlanTier {
  return PLANS[key].tier;
}

/**
 * Formats a price, for example `€8`.
 *
 * The number it is given is the whole of what is charged: prices are VAT inclusive, so nothing
 * is added to this on screen and nothing is added to it at checkout.
 */
export function formatPrice(priceCents: number, currency = 'EUR', locale = 'en-GB'): string {
  return new Intl.NumberFormat(locale, {
    style: 'currency',
    currency,
    minimumFractionDigits: priceCents % 100 === 0 ? 0 : 2,
  }).format(priceCents / 100);
}
